import {
  CompactTable,
  dangerButtonColors,
  defaultResponseHandler,
  FieldAsset,
  Flex,
  FormGroup,
  Grid,
  isaeConfigValidationSchema,
  Loading,
  openNotificationWithIcon,
  PageContent,
  PermissionGuard,
  useAssetParams,
  useAssetTranslation,
  useDebounce,
  useUserinfo,
} from '@mss/mss-component-library';
import { ErrorMessage, Formik, useFormikContext } from 'formik';
import Switch from 'formik-antd/lib/switch';
import Form from 'formik-antd/lib/form';
import Input from 'formik-antd/lib/input';
import { Modal, notification, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import { useHistory, useParams } from 'react-router-dom';
import emptyISAEConfiguration from 'jsx/asset/util/emptyISAEConfiguration';
import Api from 'jsx/asset/services/Api';
import DeleteConfigurationButton from 'jsx/asset/components/DeleteConfigurationButton';
import Button from '../../../Button';
import SubmitButton from '../../../SubmitButton';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Fetch from '../../../util/_fetch';
import { X } from 'react-feather';

export const availableDIDDiagnostics = [
  'controllability',
  'inadequate_air',
  'linkage',
  'hunting',
  'stiction',
  'failure_prediction',
  'parameter',
] as const;

type DIDDiagnosticType = (typeof availableDIDDiagnostics)[number];

export const diagnosticTypeToLabel = (
  diagnostic: DIDDiagnosticType,
  t: TFunction
) => {
  return t(diagnostic);
};

const diagnosticLabelToType = (diagnostic: string) => {
  switch (diagnostic) {
    case 'Controllability':
      return 'controllability';
    case 'Inadequate Air':
      return 'inadequate_air';
    case 'Linkage':
      return 'linkage';
    case 'Hunting':
      return 'hunting';
    case 'Stiction':
      return 'stiction';
    case 'Failure Prediction':
      return 'failure_prediction';
    case 'Parameter Monitoring':
      return 'parameter';
    default:
      return '';
  }
};

type ISAEConfiguration = {
  asset: any;
  location: string;
  device_id: string;
  device_tag: string;
  asset_config: AssetConfig;
};

type AssetConfig = {
  controllability: DidConfig;
  inadequate_air: DidConfig;
  linkage: DidConfig;
  hunting: DidConfig;
  stiction: DidConfig;
  failure_prediction: DidConfig;
  parameter: ParameterDidConfig;
};

type DidConfig = {
  enabled: boolean;
  did: string | string[];
  diagnostic: string;
};
type ParameterDidConfig = Omit<DidConfig, 'did'> & {
  did: { key: string; title: string }[];
};

const sanitizeISAEConfigForPut = (
  config: ISAEConfiguration,
  fieldAsset: FieldAsset | undefined,
  configId: string | undefined
) => {
  return fieldAsset
    ? {
        asset: config.asset,
        location: config.location,
        device_id: config.device_id
          ? config.device_id
          : fieldAsset?.asset_data.device_id,
        device_tag: config.device_tag
          ? config.device_tag
          : fieldAsset?.asset_data.device_tag,
        asset_config: config.asset_config,
        id: configId || null,
      }
    : {
        asset: config.asset,
        location: config.location,
        device_id: config.device_id,
        device_tag: config.device_tag,
        asset_config: config.asset_config,
        id: configId || null,
      };
};

const initialValues: ISAEConfiguration = emptyISAEConfiguration;

const orderCollectors = (asset_config: AssetConfig) =>
  (availableDIDDiagnostics ?? []).reduce<(DidConfig | ParameterDidConfig)[]>(
    (collectors, did) => {
      collectors.push(asset_config[did]);
      return collectors;
    },
    []
  );

const ISAEFieldAssetDetail = () => {
  const { t } = useAssetTranslation();
  const history = useHistory();
  const { assetId, assetType, assetSubType } = useAssetParams();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { configId } = useParams<{ configId: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasPermission] = useUserinfo(assetId);
  const queryKey = [
    assetType,
    assetSubType,
    assetId,
    'configuration',
    configId,
  ];
  const queryClient = useQueryClient();
  const { data: configuration = initialValues, isLoading } = useQuery({
    queryKey,
    enabled: !!configId,
    queryFn: async () => {
      const resp = (
        await defaultResponseHandler<ISAEConfiguration>({
          t,
          request: Fetch.get(
            `/api/applications/assetmanagement/isae/configuration/?id=${configId}`
          ),
        })
      ).data;

      function validateAndSetParameterDid(
        parameter: ParameterDidConfig | DidConfig
      ): ParameterDidConfig {
        if (typeof parameter.did === 'string') {
          return {
            ...parameter,
            did: parameter.did.split(',').map(d => ({ key: d, title: '' })),
          };
        }
        if (Array.isArray(parameter.did)) {
          return {
            ...parameter,
            did: parameter.did.map(d =>
              typeof d === 'string' ? { key: d, title: '' } : d
            ),
          };
        }

        throw new Error('Unknown parameter format');
      }

      return {
        ...resp,
        asset_config: {
          ...resp.asset_config,
          parameter: validateAndSetParameterDid(resp.asset_config.parameter),
        },
      };
    },
  });
  const loading = isLoading && !!configId;

  const deleteButton = (configuration: ISAEConfiguration, configId: string) => (
    <DeleteConfigurationButton
      configuration={configuration as any}
      configId={configId}
      cb={Api.assetmanagement.deleteConfig}
    />
  );

  const onSubmit = async (values: any) => {
    setIsSubmitting(true);
    const resp = await Api.assetmanagement.createConfig(
      sanitizeISAEConfigForPut(values, undefined, configId)
    );
    if (resp.status >= 200 && resp.status < 300) {
      setIsSubmitting(false);
      queryClient.invalidateQueries({ queryKey });
      history.push(
        `/applications/assetmanagement/isae/${assetId}/isae-configuration/`
      );
      openNotificationWithIcon(
        notification.success,
        !configId ? t(`Configuration added`) : t(`Configuration updated`),
        '',
        5
      );
    } else {
      setIsSubmitting(false);
      openNotificationWithIcon(
        notification.error,
        t('Adding configuration failed'),
        t(
          'The configuration could not be added. Please check the details and try again.'
        )
      );
    }
  };

  const disabled =
    loading ||
    isSubmitting ||
    !hasPermission(`change_assetmanagementapplicationisae`);

  return (
    <PageContent
      title={`${t('ISAE Configuration')}${
        configuration.device_tag ? `- ${configuration.device_tag}` : ''
      }`}
      actions={
        !loading &&
        configId && (
          <Flex>
            {hasPermission(`change_assetmanagementapplicationisae`) &&
              deleteButton(configuration, configId)}
          </Flex>
        )
      }
    >
      {loading && <Loading />}

      {!loading && (
        <Formik
          initialValues={{
            ...initialValues,
            asset: assetId,
            location: configuration.location,
            device_id: configuration.device_id,
            device_tag: configuration.device_tag,
            asset_config: configuration.asset_config,
          }}
          onSubmit={onSubmit}
          validationSchema={isaeConfigValidationSchema(t)}
          validateOnMount
        >
          {({
            values,
            errors,
            handleChange,
            handleBlur,
            setFieldValue,
            dirty,
          }) => {
            return (
              <Form>
                <PermissionGuard
                  any
                  codename={[
                    'view_fieldassetprm',
                    'add_fieldassetprm',
                    'change_fieldassetprm',
                    'delete_fieldassetprm',
                  ]}
                ></PermissionGuard>
                <Grid.Row>
                  <Grid.Col width={4}>
                    <FormGroup
                      label={t('ISAE Hierarchy location')}
                      required
                      input={
                        <Input
                          name="location"
                          required
                          disabled={disabled}
                          value={values['location']}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      }
                      error={<ErrorMessage name={`location`} />}
                    />
                  </Grid.Col>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Col width={4}>
                    <FormGroup
                      label={t('Device ID')}
                      required
                      input={
                        <Input
                          name="device_id"
                          disabled={disabled}
                          value={values['device_id']}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      }
                      error={<ErrorMessage name={`device_id`} />}
                    />
                  </Grid.Col>
                  <Grid.Col width={4}>
                    <FormGroup
                      label={t('Device Tag')}
                      required
                      input={
                        <Input
                          name="device_tag"
                          disabled={disabled}
                          value={values['device_tag']}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      }
                      error={<ErrorMessage name={`device_tag`} />}
                    />
                  </Grid.Col>
                  <Grid.Col width={4}>
                    {!configId && (
                      <>
                        <Button
                          className="w-full mt-7"
                          onClick={() => setModalOpen(true)}
                          variant="primary"
                          ghost
                        >
                          {t('Lookup device from Field Assets')}
                        </Button>
                        {modalOpen && (
                          <LookupModal setModalOpen={setModalOpen} />
                        )}
                      </>
                    )}
                  </Grid.Col>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Col>
                    <h5 className="mt-1 mb-4">{t('Diagnostics Active')}</h5>
                    <CompactTable
                      dataSource={orderCollectors(values['asset_config'])}
                      disablePointer
                      columns={columns(
                        t,
                        handleChange,
                        handleBlur,
                        setFieldValue,
                        disabled
                      )}
                      pagination={false}
                      rowKey={'diagnostic'}
                    />
                  </Grid.Col>
                </Grid.Row>
                <Flex h="end" v="center" className="mt-4">
                  <SubmitButton
                    disabled={
                      !hasPermission(`change_assetmanagementapplicationisae`) ||
                      Object.keys(errors).length > 0 ||
                      Object.values(values.asset_config)
                        .map(
                          (i: any) =>
                            i.enabled &&
                            !i.did &&
                            i.diagnostic !== 'Failure Prediction'
                        )
                        .includes(true) ||
                      !dirty
                    }
                  >
                    {t('Submit')}
                  </SubmitButton>
                </Flex>
              </Form>
            );
          }}
        </Formik>
      )}
    </PageContent>
  );
};

const autocomplete = async (modelId: string, q?: string) => {
  const url = `/api/assets/autocomplete/field/?model=assetmanagementapplicationisae&model_id=${modelId}${
    q ? `&q=${q}` : ''
  }`;

  const result = await Fetch.get(url);
  if (result.status >= 400) throw Error(result.statusText);

  return await result.json();
};

const LookupModal = ({ setModalOpen }: any) => {
  const { t } = useAssetTranslation();
  const { setFieldValue } = useFormikContext<any>();
  const [prmAssets, setPrmAssets] = useState<FieldAsset[]>([]);
  const [loadingFA, setLoadingFA] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const { assetId } = useAssetParams();
  const debouncedSearch = useDebounce(searchValue || '', 300);

  useEffect(() => {
    async function fetchData() {
      setLoadingFA(true);
      try {
        const data = await autocomplete(assetId!, String(debouncedSearch));
        setPrmAssets(data);
      } catch (e) {
        openNotificationWithIcon(
          notification.error,
          t('Unable to get field assets'),
          String(e)
        );
      } finally {
        setHasFetched(true);
        setLoadingFA(false);
      }
    }

    if (assetId && debouncedSearch) fetchData();
    if (!debouncedSearch) {
      setPrmAssets([]);
      setHasFetched(false);
    }
  }, [t, assetId, debouncedSearch]);

  const close = () => {
    setModalOpen(false);
  };

  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (searchValue && !open) {
      setOpen(true);
    }
  }, [searchValue]);

  return (
    <Modal
      title={t('Device ID and Tag lookup')}
      open
      onCancel={close}
      footer={null}
    >
      <div className="mb-4">
        {t(
          'You can search and select a Field Asset which has been onboarded to automatically populate the Device ID and Tag fields.'
        )}
      </div>
      <Select
        className={'mb-6'}
        autoFocus
        loading={loadingFA}
        placeholder={`${t('Search for a Field Asset')}`}
        showSearch
        style={{ width: '100%' }}
        onSearch={setSearchValue}
        allowClear={false}
        searchValue={searchValue}
        open={hasFetched && open}
        filterOption={false}
        options={prmAssets.map((prmAsset: FieldAsset) => ({
          value: prmAsset.id,
          label: prmAsset.name,
        }))}
        onSelect={(id: string) => {
          const asset = prmAssets.find(asset => asset.id === id);
          if (asset) {
            setFieldValue('device_id', asset?.asset_data.device_id);
            setFieldValue('device_tag', asset?.asset_data.device_tag);
            close();
          }
        }}
        data-ta="assetPrmServerInput"
      />
    </Modal>
  );
};

const columns = (
  t: TFunction,
  onChange: any,
  onBlur: any,
  setFieldValue: any,
  disabled: boolean
) => [
  {
    title: t('Status'),
    dataIndex: 'enabled',
    width: 64,
    onCell: () => ({ style: { verticalAlign: 'top' } }),
    render: (active: boolean, row: any) => {
      return (
        <div style={{ textAlign: 'center' }}>
          <Switch
            size="small"
            disabled={disabled}
            name={`asset_config.${diagnosticLabelToType(
              row.diagnostic
            )}.enabled`}
            checked={row.enabled}
            onChange={(checked: boolean) => {
              setFieldValue(
                `asset_config.${diagnosticLabelToType(row.diagnostic)}.enabled`,
                checked
              );
              if (
                diagnosticLabelToType(row.diagnostic) === 'parameter' &&
                row.did.length === 0
              ) {
                setFieldValue(
                  `asset_config.${diagnosticLabelToType(row.diagnostic)}.did`,
                  [{ key: '', title: '' }],
                  false
                );
              }
            }}
          />
        </div>
      );
    },
  },
  {
    title: t('Diagnostic'),
    dataIndex: 'diagnostic',
    width: 400,
    onCell: () => ({ style: { verticalAlign: 'top' } }),
    render: (diagnostic: DIDDiagnosticType) =>
      diagnosticTypeToLabel(diagnostic, t),
  },
  {
    title: t('DID Name'),
    dataIndex: 'did',
    align: 'top',
    render: (did_name: string, row: DidConfig | ParameterDidConfig) => {
      const baseProps = {
        onChange,
        onBlur,
        name: `asset_config.${diagnosticLabelToType(row.diagnostic)}.did`,
        required: row.enabled,
      };

      const isParameter = diagnosticLabelToType(row.diagnostic) === 'parameter';

      return isParameter && typeof row.did !== 'string' ? (
        <div>
          {row.did.length > 0 && (
            <table className="mb-3">
              <thead>
                <tr className="font-bold">
                  <td className="pb-1">{t('Parameter')}</td>
                  <td className="pb-1">{t('Parameter title')}</td>
                  <td />
                </tr>
              </thead>
              <tbody>
                {row.did.map((d, i) => {
                  const did = d as { key: string; title: string };
                  const names = {
                    key: `${baseProps.name}[${i}].key`,
                    title: `${baseProps.name}[${i}].title`,
                  };
                  return (
                    <tr key={i}>
                      <td className="w-1/4 pr-2">
                        <Input
                          {...baseProps}
                          name={names.key}
                          status={!did.key && row.enabled ? 'error' : ''}
                        />
                      </td>
                      <td className="w-3/4">
                        <Input
                          {...baseProps}
                          name={names.title}
                          status={!did.title && row.enabled ? 'error' : ''}
                        />
                      </td>
                      <td>
                        <Button
                          ghost
                          colorOverrides={{
                            ...dangerButtonColors,
                            border: 'transparent',
                          }}
                          onClick={() => {
                            if (typeof row.did !== 'string') {
                              setFieldValue(
                                baseProps.name,
                                row.did.filter((_, idx) => idx !== i),
                                true
                              );

                              if (row.did.length === 1) {
                                // setTimeout to make it sequential and avoid react render batching
                                setTimeout(
                                  () =>
                                    setFieldValue(
                                      `asset_config.${diagnosticLabelToType(
                                        row.diagnostic
                                      )}.enabled`,
                                      false
                                    ),
                                  16
                                );
                              }
                            }
                          }}
                        >
                          <Flex h="center" v="center">
                            <X size={16} />
                          </Flex>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="text-right">
            <Button
              onClick={() => {
                setFieldValue(
                  baseProps.name,
                  [
                    ...row.did,
                    {
                      key: '',
                      title: '',
                    },
                  ],
                  true
                );
                if (!row.did.length) {
                  // setTimeout to make it sequential and avoid react render batching
                  setTimeout(
                    () =>
                      setFieldValue(
                        `asset_config.${diagnosticLabelToType(row.diagnostic)}.enabled`,
                        true
                      ),
                    16
                  );
                }
              }}
            >
              {t('Add mapping')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <Input
            {...baseProps}
            status={
              row.enabled && !row.did && row.diagnostic !== 'Failure Prediction'
                ? 'error'
                : ''
            }
            disabled={disabled || row.diagnostic === 'Failure Prediction'}
            value={
              row.diagnostic === 'Failure Prediction'
                ? 'Basic_Prediction'
                : did_name
            }
            validate={value =>
              value.length < 51 ? undefined : t('Max 50 characters')
            }
          />
          <ErrorMessage
            render={msg =>
              msg !== 'Required' ? (
                <div className={'text-xs mt-2 mb-0 mx-3 text-[#fa4654]'}>
                  {msg}
                </div>
              ) : null
            }
            name={`asset_config.${diagnosticLabelToType(row.diagnostic)}.did`}
          />
        </>
      );
    },
  },
];

export default ISAEFieldAssetDetail;
